import { CopyId } from "@/components/copy-id";
import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useSheetMode } from "@/hooks/use-sheet-mode";
import { queryKeys } from "@/lib/queryKeys";
import {
  photoSpotsProvider,
  type PhotoSpotListItem,
} from "@/providers/photo-spots.provider";
import { usersProvider, type User } from "@/providers/users.provider";
import {
  IconCamera,
  IconChevronLeft,
  IconChevronRight,
  IconPencil,
  IconPhoto,
  IconPlus,
  IconUpload,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useRef, useState } from "react";
import { toast } from "sonner";

function SpotCard({ spot, onEdit }: { spot: PhotoSpotListItem; onEdit: (spot: PhotoSpotListItem) => void }) {
  return (
    <div className="border rounded-lg overflow-hidden flex flex-col text-sm bg-card">
      <div className="relative group">
        {spot.img_obj_url ? (
          <img
            src={spot.img_obj_url}
            alt={spot.name}
            className="h-36 w-full object-cover"
          />
        ) : (
          <div className="h-36 bg-muted flex items-center justify-center">
            <IconCamera className="size-8 text-muted-foreground/40" />
          </div>
        )}
        <button
          onClick={() => onEdit(spot)}
          className="absolute top-2 right-2 size-7 rounded-md bg-background/80 backdrop-blur-sm border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
        >
          <IconPencil className="size-3.5" />
        </button>
      </div>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium leading-tight">{spot.name}</span>
          {!spot.is_active && (
            <Badge variant="secondary" className="shrink-0 text-xs">
              Inactive
            </Badge>
          )}
        </div>
        {spot.description && (
          <p className="text-muted-foreground text-xs line-clamp-2">
            {spot.description}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-auto pt-1.5 border-t">
          {spot.distance_meters !== undefined && (
            <span>{(spot.distance_meters / 1000).toFixed(2)} km</span>
          )}
          <span className="font-mono tabular-nums">
            {spot.lat.toFixed(4)}, {spot.lng.toFixed(4)}
          </span>
          <span>{new Date(spot.created_at).toLocaleDateString()}</span>
        </div>
        <CopyId id={spot.spot_id} />
      </div>
    </div>
  );
}

function SpotGrid({
  spots,
  isLoading,
  isError,
  emptyMessage,
  onEdit,
}: {
  spots: PhotoSpotListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  emptyMessage: string;
  onEdit: (spot: PhotoSpotListItem) => void;
}) {
  if (isLoading)
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-52 rounded-lg" />
        ))}
      </div>
    );
  if (isError)
    return <p className="text-sm text-muted-foreground">Failed to load photo spots.</p>;
  if (!spots?.length)
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {spots.map((spot) => (
        <SpotCard key={spot.spot_id} spot={spot} onEdit={onEdit} />
      ))}
    </div>
  );
}

function Pagination({
  page,
  hasNext,
  onPrev,
  onNext,
}: {
  page: number;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (page === 1 && !hasNext) return null;
  return (
    <div className="flex items-center gap-4">
      <Button variant="outline" size="sm" disabled={page === 1} onClick={onPrev} className="gap-1">
        <IconChevronLeft className="size-3.5" />
        Previous
      </Button>
      <span className="text-sm text-muted-foreground">Page {page}</span>
      <Button variant="outline" size="sm" disabled={!hasNext} onClick={onNext} className="gap-1">
        Next
        <IconChevronRight className="size-3.5" />
      </Button>
    </div>
  );
}

// ── Near location tab ─────────────────────────────────────────────────────────

function NearTab({ onEdit }: { onEdit: (spot: PhotoSpotListItem) => void }) {
  const [latInput, setLatInput] = useState("");
  const [lngInput, setLngInput] = useState("");
  const [radiusInput, setRadiusInput] = useState("5000");
  const [searchParams, setSearchParams] = useState<{
    lat: number;
    lng: number;
    radius_m: number;
  } | null>(null);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: searchParams
      ? [...queryKeys.photoSpots(searchParams.lat, searchParams.lng, searchParams.radius_m), page]
      : ["photo-spots-near-disabled"],
    queryFn: () =>
      photoSpotsProvider.listNear(
        searchParams!.lat,
        searchParams!.lng,
        searchParams!.radius_m,
        page,
      ),
    enabled: searchParams !== null,
  });

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      toast.error("Latitude must be between -90 and 90");
      return;
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      toast.error("Longitude must be between -180 and 180");
      return;
    }
    const radius_m = parseFloat(radiusInput);
    setPage(1);
    setSearchParams({
      lat,
      lng,
      radius_m: isNaN(radius_m) ? 5000 : Math.min(radius_m, 50000),
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={handleSearch} className="flex items-end gap-2 flex-wrap">
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Latitude</Label>
          <Input
            value={latInput}
            onChange={(e) => setLatInput(e.target.value)}
            placeholder="37.7749"
            className="w-32"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Longitude</Label>
          <Input
            value={lngInput}
            onChange={(e) => setLngInput(e.target.value)}
            placeholder="-122.4194"
            className="w-32"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Radius (m)</Label>
          <Input
            value={radiusInput}
            onChange={(e) => setRadiusInput(e.target.value)}
            placeholder="5000"
            className="w-24"
          />
        </div>
        <Button type="submit" disabled={isLoading}>
          Search
        </Button>
      </form>

      {!searchParams ? (
        <p className="text-sm text-muted-foreground">
          Enter coordinates to find photo spots nearby.
        </p>
      ) : (
        <>
          <SpotGrid
            spots={data?.data}
            isLoading={isLoading}
            isError={isError}
            emptyMessage="No photo spots found in this area."
            onEdit={onEdit}
          />
          <Pagination
            page={page}
            hasNext={data?.has_next ?? false}
            onPrev={() => setPage((p) => p - 1)}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}
    </div>
  );
}

// ── By user tab ───────────────────────────────────────────────────────────────

function ByUserTab({ onEdit }: { onEdit: (spot: PhotoSpotListItem) => void }) {
  const [usernameInput, setUsernameInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [page, setPage] = useState(1);

  const {
    data: userResults,
    isFetching: userSearchFetching,
  } = useQuery({
    queryKey: queryKeys.usersSearch(searchTerm),
    queryFn: () => usersProvider.search(searchTerm),
    enabled: searchTerm.length >= 2,
    staleTime: 30_000,
  });

  const { data: spotsData, isLoading: spotsLoading, isError: spotsError } = useQuery({
    queryKey: queryKeys.photoSpotsByUser(selectedUser?.user_id ?? "", page),
    queryFn: () => photoSpotsProvider.listByUser(selectedUser!.user_id, page),
    enabled: selectedUser !== null,
  });

  function handleUserSearch(e: FormEvent) {
    e.preventDefault();
    const q = usernameInput.trim();
    if (q.length < 2) {
      toast.error("Enter at least 2 characters");
      return;
    }
    setSearchTerm(q);
  }

  function selectUser(user: User) {
    setSelectedUser(user);
    setPage(1);
    setSearchTerm("");
    setUsernameInput("");
  }

  function clearUser() {
    setSelectedUser(null);
    setPage(1);
  }

  const showResults = searchTerm.length >= 2 && !selectedUser;

  return (
    <div className="flex flex-col gap-4">
      {selectedUser ? (
        <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/40 w-fit">
          <div className="flex items-center gap-2">
            <IconUser className="size-4 text-muted-foreground" />
            <span className="font-medium text-sm">{selectedUser.username}</span>
          </div>
          <CopyId id={selectedUser.user_id} />
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={clearUser}
          >
            <IconX className="size-3.5" />
          </Button>
        </div>
      ) : (
        <form onSubmit={handleUserSearch} className="flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Username</Label>
            <Input
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              placeholder="Search username…"
              className="w-56"
              autoComplete="off"
            />
          </div>
          <Button type="submit" disabled={userSearchFetching}>
            {userSearchFetching ? "Searching…" : "Search"}
          </Button>
        </form>
      )}

      {showResults && (
        <div className="flex flex-col gap-1 w-fit">
          {userSearchFetching ? (
            <div className="flex flex-col gap-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-72 rounded-md" />
              ))}
            </div>
          ) : !userResults?.length ? (
            <p className="text-sm text-muted-foreground">No users found.</p>
          ) : (
            userResults.map((user) => (
              <button
                key={user.user_id}
                onClick={() => selectUser(user)}
                className="flex items-center gap-3 px-3 py-2 rounded-md border bg-card hover:bg-muted text-left text-sm transition-colors w-72"
              >
                <IconUser className="size-4 text-muted-foreground shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="font-medium truncate">{user.username}</span>
                  <span className="text-xs text-muted-foreground font-mono truncate">
                    {user.user_id}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {selectedUser && (
        <>
          <SpotGrid
            spots={spotsData?.data}
            isLoading={spotsLoading}
            isError={spotsError}
            emptyMessage={`${selectedUser.username} has no photo spots.`}
            onEdit={onEdit}
          />
          <Pagination
            page={page}
            hasNext={spotsData?.has_next ?? false}
            onPrev={() => setPage((p) => p - 1)}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type SheetMode = { type: "create" } | { type: "edit"; spot: PhotoSpotListItem };

export function PhotoSpotsPage() {
  const queryClient = useQueryClient();
  const { mode, isOpen, setMode, close } = useSheetMode<SheetMode>();

  // Create form state
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createLat, setCreateLat] = useState("");
  const [createLng, setCreateLng] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadStep, setUploadStep] = useState<"idle" | "creating" | "uploading">("idle");
  const fileRef = useRef<HTMLInputElement>(null);

  // Edit image state
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editUploading, setEditUploading] = useState(false);
  const editFileRef = useRef<HTMLInputElement>(null);

  function resetForm() {
    setCreateName("");
    setCreateDesc("");
    setCreateLat("");
    setCreateLng("");
    setImageFile(null);
    setUploadStep("idle");
  }

  function resetEdit() {
    setEditImageFile(null);
    setEditUploading(false);
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      setUploadStep("creating");
      const spot = await photoSpotsProvider.create({
        name: createName.trim(),
        description: createDesc.trim() || undefined,
        lat: parseFloat(createLat),
        lng: parseFloat(createLng),
      });
      if (imageFile) {
        setUploadStep("uploading");
        const uploadUrl = await photoSpotsProvider.requestImageUpload(spot.spot_id);
        await photoSpotsProvider.uploadImageToStorage(uploadUrl, imageFile);
      }
    },
    onSuccess: () => {
      toast.success("Photo spot created");
      close();
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["photo-spots"] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
      setUploadStep("idle");
    },
  });

  const editUploadMutation = useMutation({
    mutationFn: async (spotId: string) => {
      setEditUploading(true);
      const uploadUrl = await photoSpotsProvider.requestImageUpload(spotId);
      await photoSpotsProvider.uploadImageToStorage(uploadUrl, editImageFile!);
    },
    onSuccess: () => {
      toast.success("Image uploaded");
      close();
      resetEdit();
      queryClient.invalidateQueries({ queryKey: ["photo-spots"] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
      setEditUploading(false);
    },
  });

  const createValid =
    createName.trim().length > 0 &&
    createName.trim().length <= 100 &&
    !isNaN(parseFloat(createLat)) &&
    !isNaN(parseFloat(createLng));

  const submitLabel =
    uploadStep === "creating"
      ? "Creating…"
      : uploadStep === "uploading"
        ? "Uploading image…"
        : "Create Spot";

  function handleEdit(spot: PhotoSpotListItem) {
    resetEdit();
    setMode({ type: "edit", spot });
  }

  const editSpot = mode.type === "edit" ? mode.spot : null;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Photo Spots" />

      <div className="flex flex-col gap-4 flex-1 p-4 lg:p-6 overflow-y-auto">
        <div className="flex items-center justify-between">
          <Tabs defaultValue="near" className="w-full">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <TabsList>
                <TabsTrigger value="near">Near Location</TabsTrigger>
                <TabsTrigger value="by-user">By User</TabsTrigger>
              </TabsList>
              <Button onClick={() => setMode({ type: "create" })} className="gap-1.5">
                <IconPlus className="size-4" />
                Create
              </Button>
            </div>

            <TabsContent value="near" className="mt-0">
              <NearTab onEdit={handleEdit} />
            </TabsContent>
            <TabsContent value="by-user" className="mt-0">
              <ByUserTab onEdit={handleEdit} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Create sheet */}
      <Sheet open={mode.type === "create"} onOpenChange={(open) => { if (!open) { close(); resetForm(); } }}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Create Photo Spot</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-col gap-1.5">
              <Label>Name *</Label>
              <Input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Sunset Ridge"
                maxLength={100}
              />
              <span className="text-xs text-muted-foreground text-right">
                {createName.length}/100
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Description</Label>
              <Textarea
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
                placeholder="Optional description…"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Latitude *</Label>
                <Input
                  value={createLat}
                  onChange={(e) => setCreateLat(e.target.value)}
                  placeholder="37.7749"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Longitude *</Label>
                <Input
                  value={createLng}
                  onChange={(e) => setCreateLng(e.target.value)}
                  placeholder="-122.4194"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Cover image</Label>
              <div className="flex items-center gap-3">
                <div
                  className="size-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => fileRef.current?.click()}
                >
                  {imageFile ? (
                    <img
                      src={URL.createObjectURL(imageFile)}
                      alt="preview"
                      className="size-full object-cover"
                    />
                  ) : (
                    <IconPhoto className="size-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                    className="gap-1.5"
                  >
                    <IconUpload className="size-3.5" />
                    {imageFile ? "Change image" : "Choose image"}
                  </Button>
                  {imageFile && (
                    <button
                      type="button"
                      onClick={() => setImageFile(null)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors text-left"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  if (file && file.size > 5 * 1024 * 1024) {
                    toast.error("Image must be under 5 MB");
                    return;
                  }
                  setImageFile(file);
                  e.target.value = "";
                }}
              />
            </div>

            <Button
              disabled={!createValid || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {submitLabel}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit image sheet */}
      <Sheet open={mode.type === "edit"} onOpenChange={(open) => { if (!open) { close(); resetEdit(); } }}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Photo Spot</SheetTitle>
          </SheetHeader>
          {editSpot && (
            <div className="flex flex-col gap-4 p-4">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">{editSpot.name}</span>
                <span className="text-xs text-muted-foreground font-mono">{editSpot.spot_id}</span>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>Cover image</Label>
                <div className="flex items-center gap-3">
                  <div
                    className="size-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => editFileRef.current?.click()}
                  >
                    {editImageFile ? (
                      <img
                        src={URL.createObjectURL(editImageFile)}
                        alt="preview"
                        className="size-full object-cover"
                      />
                    ) : editSpot.img_obj_url ? (
                      <img
                        src={editSpot.img_obj_url}
                        alt={editSpot.name}
                        className="size-full object-cover"
                      />
                    ) : (
                      <IconPhoto className="size-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => editFileRef.current?.click()}
                      className="gap-1.5"
                    >
                      <IconUpload className="size-3.5" />
                      {editImageFile ? "Change image" : editSpot.img_obj_url ? "Replace image" : "Choose image"}
                    </Button>
                    {editImageFile && (
                      <button
                        type="button"
                        onClick={() => setEditImageFile(null)}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors text-left"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <input
                  ref={editFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    if (file && file.size > 5 * 1024 * 1024) {
                      toast.error("Image must be under 5 MB");
                      return;
                    }
                    setEditImageFile(file);
                    e.target.value = "";
                  }}
                />
              </div>

              <Button
                disabled={!editImageFile || editUploadMutation.isPending}
                onClick={() => editUploadMutation.mutate(editSpot.spot_id)}
              >
                {editUploading ? "Uploading…" : "Upload Image"}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
